const VALIDATORS = {
  required: (value, { message = 'This field is required' } = {}) => {
    if (value === undefined || value === null) return message;
    if (typeof value === 'string' && !value.trim()) return message;
    if (typeof value === 'number' && isNaN(value)) return message;
    return null;
  },

  notEmpty: (value, { message = 'This field cannot be empty' } = {}) => {
    if (typeof value === 'string' && !value.trim()) return message;
    return null;
  },

  maxLength: (value, max, { message } = {}) => {
    if (!value) return null;
    const msg = message || `Must be ${max} characters or fewer`;
    if (typeof value === 'string' && value.length > max) return msg;
    return null;
  },

  minLength: (value, min, { message } = {}) => {
    if (!value) return null;
    const msg = message || `Must be at least ${min} characters`;
    if (typeof value === 'string' && value.trim().length < min) return msg;
    return null;
  },

  min: (value, min, { message } = {}) => {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    if (isNaN(num)) return 'Must be a valid number';
    if (num < min) return message || `Must be at least ${min}`;
    return null;
  },

  max: (value, max, { message } = {}) => {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    if (isNaN(num)) return 'Must be a valid number';
    if (num > max) return message || `Must be at most ${max}`;
    return null;
  },

  email: (value, { message = 'Please enter a valid email address' } = {}) => {
    if (!value || !value.trim()) return null;
    if (!value.includes('@')) return message;
    const domain = value.split('@')[1];
    if (!domain || !domain.includes('.')) return message;
    return null;
  },

  gstin: (value, { message, required = false } = {}) => {
    if (!value || !value.trim()) return required ? 'GSTIN is required' : null;
    const cleaned = value.trim().toUpperCase();
    if (cleaned.length !== 15) return message || 'GSTIN must be exactly 15 characters';
    return null;
  },

  mobile: (value, { message, allowEmpty = true } = {}) => {
    if (!value || !value.trim()) return allowEmpty ? null : 'Mobile number is required';
    const cleaned = value.trim().replace(/\D/g, '');
    if (cleaned.length !== 10) return message || 'Mobile number must be 10 digits';
    return null;
  },

  username: (value) => {
    if (!value) return 'Username is required';
    if (value.trim().length < 2) return 'Username must be at least 2 characters';
    const regex = /^[a-z0-9_]+$/;
    if (!regex.test(value.toLowerCase())) {
      return 'Username can only contain letters, numbers, and underscores';
    }
    return null;
  },

  password: (value, { minLength = 4, message } = {}) => {
    if (!value) return 'Password is required';
    if (value.length < minLength) return message || `Password must be at least ${minLength} characters`;
    return null;
  },

  match: (value, otherValue, { message = 'Values do not match' } = {}) => {
    if (value !== otherValue) return message;
    return null;
  },

  vehicleNumber: (value, { message = 'Vehicle number is required' } = {}) => {
    if (!value || !value.trim()) return message;
    return null;
  },

  quantityPositive: (value, { message = 'Enter a valid quantity (1 or more)' } = {}) => {
    if (value === null || value === undefined || value === '') return 'Quantity is required';
    const num = Number(value);
    if (isNaN(num) || num < 1) return message;
    return null;
  },

  catalogItemName: (value, { message = 'Item name is required' } = {}) => {
    if (!value || !value.trim()) return message;
    return null;
  },

  amount: (value, { message = 'Please enter a valid amount' } = {}) => {
    if (value === null || value === undefined || value === '') return 'Amount is required';
    const num = Number(value);
    if (isNaN(num) || num <= 0) return message;
    return null;
  },

  mode: (value, { message = 'Payment mode is required' } = {}) => {
    if (!value || !value.trim()) return message;
    return null;
  },

  stageName: (value, { message = 'Stage name is required' } = {}) => {
    if (!value || !value.trim()) return message;
    if (value.trim().length > 100) return 'Stage name must be 100 characters or fewer';
    return null;
  },

  machineName: (value, { message = 'Machine name is required' } = {}) => {
    if (!value || !value.trim()) return message;
    return null;
  },

  colorHex: (value, { message = 'Must be a valid hex color (e.g. #FF0000)' } = {}) => {
    if (!value || !value.trim()) return null;
    const regex = /^#[0-9A-Fa-f]{6}$/;
    if (!regex.test(value.trim())) return message;
    return null;
  },

  roleNote: (value) => {
    if (value && value.length > 100) return 'Role note must be 100 characters or fewer';
    return null;
  },

  remarks: (value, { message = 'Please add a remark' } = {}) => {
    if (!value || !value.trim()) return message;
    return null;
  },
};

export function runValidators(value, rules) {
  if (!rules || rules.length === 0) return null;
  for (const rule of rules) {
    const error = typeof rule === 'function' ? rule(value) : VALIDATORS[rule](value);
    if (error) return error;
  }
  return null;
}

export function validateField(fieldName, value, validators) {
  if (!validators || validators.length === 0) return null;
  for (const v of validators) {
    let error = null;
    if (typeof v === 'function') {
      error = v(value);
    } else if (typeof v === 'object' && v.name) {
      const fn = VALIDATORS[v.name];
      if (fn) error = fn(value, v.options || {});
    } else if (typeof v === 'string') {
      const fn = VALIDATORS[v];
      if (fn) error = fn(value);
    }
    if (error) return error;
  }
  return null;
}

export function validateForm(fields, onFieldError) {
  let isValid = true;
  const errors = {};
  for (const [name, { value, validators }] of Object.entries(fields)) {
    const error = validateField(name, value, validators);
    if (error) {
      isValid = false;
      errors[name] = error;
      if (onFieldError) onFieldError(name, error);
    }
  }
  return { isValid, errors };
}

// refusals the API states as a stable token so the client can branch on them.
// Shown as-is they read as an error code, so each needs its own sentence.
const DETAIL_MESSAGES = {
  network_not_allowed: 'This account can only be used from the office network.',
  password_change_required: 'Please set a new password before continuing.',
};

export function formatApiError(err, fallback = 'Something went wrong') {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ');
  }
  if (DETAIL_MESSAGES[detail]) return DETAIL_MESSAGES[detail];
  return typeof detail === 'string' ? detail : fallback;
}

export default VALIDATORS;
